// web-frontend/src/components/modals/BusinessUpgradeModal.jsx
// 🔥 [2026-04-12] i18n 다국어 지원 — 모든 사용자 노출 텍스트를 i18nService.t() 로 처리.
// 이전에는 한글 하드코딩으로 영문/일문/중문 사용자가 신청 불가 상태였음.

import React, { useState } from 'react';
import {
  Building,
  Briefcase,
  FileText,
  Upload,
  X,
  Loader2,
  User,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import fileService from '../../shared/services/fileService';
import authService from '../../shared/services/authService';
import useAuthStore from '../../shared/stores/authStore';
import i18nService from '../../shared/services/i18n';
import './BusinessUpgradeModal.css';

// 🔥 [2026-05-30] intent(buyer/exhibitor) 수신 — SPC 브릿지에서 전달. 현재는 user_type=BUSINESS_VISITOR
// 로 통일(승인 모델 보존). buyer→즉시 BUYER 부여 여부는 백엔드 정책 결정 후 반영 예정.
const BusinessUpgradeModal = ({ isOpen, intent = null, onClose }) => {
  const { user, setUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const t = (key, params) => i18nService.t(key, params);

  // 입력 폼 상태
  const [formData, setFormData] = useState({
    company_name: user?.company_name || '',
    department: user?.department || '',
    job_title: user?.job_title || '',
  });

  // 파일 상태
  const [businessCard, setBusinessCard] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [cardPreview, setCardPreview] = useState(null);
  const [profilePreview, setProfilePreview] = useState(user?.profile_image || null);

  // 유효성 검사 상태
  const [validationErrors, setValidationErrors] = useState({
    company_name: false,
    job_title: false,
    businessCard: false,
    profileImage: false,
  });

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: false }));
    }
    setError('');
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError(t('businessUpgrade.error_file_size'));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError(t('businessUpgrade.error_file_type'));
      return;
    }

    if (type === 'card') {
      setBusinessCard(file);
      setCardPreview(URL.createObjectURL(file));
      setValidationErrors(prev => ({ ...prev, businessCard: false }));
    } else {
      setProfileImage(file);
      setProfilePreview(URL.createObjectURL(file));
      setValidationErrors(prev => ({ ...prev, profileImage: false }));
    }
    setError('');
  };

  const validateForm = () => {
    const errors = {
      company_name: !formData.company_name.trim(),
      job_title: !formData.job_title.trim(),
      businessCard: !businessCard && !user?.business_card_url,
      profileImage: !profileImage && !user?.profile_image,
    };
    setValidationErrors(errors);

    if (errors.company_name) {
      setError(t('businessUpgrade.company_error'));
      return false;
    }
    if (errors.job_title) {
      setError(t('businessUpgrade.job_title_error'));
      return false;
    }
    if (errors.profileImage) {
      setError(t('businessUpgrade.error_profile_required'));
      return false;
    }
    if (errors.businessCard) {
      setError(t('businessUpgrade.error_card_required'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      let cardUrl = user?.business_card_url;
      let profileUrl = user?.profile_image;

      if (businessCard || profileImage) {
        setUploading(true);
        if (profileImage) {
          const res = await fileService.upload(profileImage, 'profile_image');
          profileUrl = res.url;
        }
        if (businessCard) {
          const res = await fileService.upload(businessCard, 'business_card');
          cardUrl = res.url;
        }
        setUploading(false);
      }

      const updateData = {
        ...formData,
        business_card_url: cardUrl,
        profile_image: profileUrl,
        user_type: 'BUSINESS_VISITOR'
      };

      const updatedUser = await authService.updateProfile(updateData);
      setUser(updatedUser);
      setSuccessMessage(t('businessUpgrade.success_toast'));

      setTimeout(() => {
        setShowSuccessModal(true);
      }, 500);

    } catch (err) {
      console.error(err);
      setError(t('businessUpgrade.error_submit'));
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const getInputClassName = (fieldName) => {
    return validationErrors[fieldName] ? 'form-input has-error' : 'form-input';
  };

  return (
    <div className="business-upgrade-modal modal-overlay">
      <div className="modal-card">

        {/* 헤더 */}
        <div className="modal-header">
          <button
            className="modal-close-btn"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <h2 className="modal-title">{t('businessUpgrade.title')}</h2>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="message-box error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* 성공 메시지 */}
        {successMessage && (
          <div className="message-box success">
            <CheckCircle size={18} />
            <span>{successMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form">

          {/* 설명 텍스트 */}
          <p className="modal-description">
            {t('businessUpgrade.description').split('\n').map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </p>

          {/* 1. 프로필 이미지 */}
          <div className="profile-section">
            <label className="profile-label">
              {t('businessUpgrade.profile_label')} <span className="required">{t('businessUpgrade.profile_required')}</span>
            </label>
            <div className="profile-upload-container">
              <div className={`profile-preview ${validationErrors.profileImage ? 'has-error' : ''}`}>
                {profilePreview ? (
                  <img src={profilePreview} alt="Profile" />
                ) : (
                  <div className="profile-placeholder">
                    <User size={36} className="profile-placeholder-icon" />
                    <p className="profile-placeholder-text">{t('businessUpgrade.profile_placeholder')}</p>
                  </div>
                )}
              </div>
              <label
                htmlFor="profileUpload"
                className={`profile-upload-btn ${validationErrors.profileImage ? 'has-error' : ''}`}
              >
                <Upload size={14} />
              </label>
              <input
                id="profileUpload"
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'profile')}
              />
            </div>

            {validationErrors.profileImage && (
              <p className="profile-error-text">{t('businessUpgrade.profile_error')}</p>
            )}
          </div>

          {/* 2. 텍스트 입력 필드 */}
          <div className="input-group">
            <Building className="input-icon" size={18} />
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              placeholder={t('businessUpgrade.company_placeholder')}
              className={getInputClassName('company_name')}
              required
            />
          </div>
          {validationErrors.company_name && (
            <p className="input-error-text">{t('businessUpgrade.company_error')}</p>
          )}

          <div className="input-group">
            <Briefcase className="input-icon" size={18} />
            <input
              type="text"
              name="department"
              value={formData.department}
              onChange={handleChange}
              placeholder={t('businessUpgrade.department_placeholder')}
              className="form-input"
            />
          </div>

          <div className="input-group">
            <FileText className="input-icon" size={18} />
            <input
              type="text"
              name="job_title"
              value={formData.job_title}
              onChange={handleChange}
              placeholder={t('businessUpgrade.job_title_placeholder')}
              className={getInputClassName('job_title')}
              required
            />
          </div>
          {validationErrors.job_title && (
            <p className="input-error-text">{t('businessUpgrade.job_title_error')}</p>
          )}

          {/* 3. 명함 업로드 */}
          <div className={`card-upload-section ${validationErrors.businessCard ? 'has-error' : ''}`}>
            <label className="card-upload-label">
              <Upload size={16} />
              {t('businessUpgrade.card_label')}
              <span className="required">{t('businessUpgrade.card_required')}</span>
            </label>

            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'card')}
              className="card-file-input"
            />

            {cardPreview && (
              <div className="card-preview">
                <img src={cardPreview} alt="Card Preview" />
              </div>
            )}

            {validationErrors.businessCard && !cardPreview && (
              <p className="card-error-text">{t('businessUpgrade.card_error')}</p>
            )}
          </div>

          {/* 안내 문구 */}
          <div className="notice-box">
            <p>
              💡 <strong>{t('businessUpgrade.notice').split('.')[0]}.</strong>{' '}
              {t('businessUpgrade.notice').split('.').slice(1).join('.').trim()}
            </p>
          </div>

          {/* 버튼 */}
          <div className="button-row">
            <button
              type="button"
              onClick={onClose}
              className="btn-cancel"
            >
              {t('businessUpgrade.btn_cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="btn-submit"
            >
              {loading || uploading ? (
                <>
                  <Loader2 className="spin" size={18} />
                  {uploading ? t('businessUpgrade.btn_uploading') : t('businessUpgrade.btn_processing')}
                </>
              ) : (
                t('businessUpgrade.btn_submit')
              )}
            </button>
          </div>
        </form>
      </div>

      {/* 성공 모달 */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-card">
            <div className="success-modal-header">
              <h3 className="success-modal-title">
                <CheckCircle size={22} color="#10b981" />
                {t('businessUpgrade.success_title')}
              </h3>
              <button
                onClick={() => { setShowSuccessModal(false); onClose(); }}
                className="success-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            <p className="success-modal-message">
              {t('businessUpgrade.success_message').split('\n').map((line, i) => (
                <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
              ))}
            </p>

            <button
              onClick={() => { setShowSuccessModal(false); onClose(); }}
              className="success-modal-btn"
            >
              {t('businessUpgrade.success_confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessUpgradeModal;
